import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import InputTextModal from "../../components/clientServiceSubscriptionTask/modalGeneralComponents/InputTextModal";
import ButtonModal from "../../components/clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/ButtonModal";
import { isValidLink } from "../../helpers/urlRegexValidator";

const AddNewItemToJobModal = ({ open, setOpen, job, setJob }) => {
    const completeButtonRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);

    // Single optional link
    const [newLinkUrl, setNewLinkUrl] = useState("");
    const [newChecklistItem, setNewChecklistItem] = useState({
        id: "0",
        description: "",
        links: [],
        notes: "",
        order: 0,
    });

    const handleChangeDescription = (e) => {
        setNewChecklistItem((prev) => ({
            ...prev,
            description: e.target.value,
        }));
    };

    const handleChangeLink = (e) => {
        const url = e.target.value;
        setNewLinkUrl(url);
        const linkObj = { name: url, url };
        setNewChecklistItem((prev) => ({
            ...prev,
            links: url.trim() ? [linkObj] : [],
        }));
    };

    const onHandleSubmit = async (e) => {
        e.preventDefault();
        if (newChecklistItem.description.trim() === "") {
            toast.error("Name is required.");
            return;
        }
        if (newLinkUrl.trim() && !isValidLink(newLinkUrl)) {
            toast.error("Please enter a valid URL.");
            return;
        }

        setIsSaving(true);
        const nextIndex = job.checklistItems.length + 1;
        const itemToAdd = {
            ...newChecklistItem,
            id: nextIndex.toString(),
            order: nextIndex,
        };

        setJob((prev) => ({
            ...prev,
            checklistItems: [...prev.checklistItems, itemToAdd],
        }));

        // Reset form and close
        setNewChecklistItem({
            id: "0",
            description: "",
            links: [],
            notes: "",
            order: 0,
        });
        setNewLinkUrl("");
        setIsSaving(false);
        setOpen(false);
    };

    const onHandleCancel = () => {
        setOpen(false);
    };

    return (
        <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={onHandleCancel}
                    className="fixed inset-0 z-50 flex items-center justify-center"
                >
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                    {/* Full-screen panel */}
                    <Dialog.Panel className="fixed inset-0 overflow-y-auto bg-white">
                        <div className="flex items-center justify-between p-4 border-b">
                            <Dialog.Title className="text-lg font-medium text-gray-900">
                                Add Custom Checklist Item
                            </Dialog.Title>
                            <button
                                onClick={onHandleCancel}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={onHandleSubmit} className="p-6 space-y-6">
                            {/* Frontend label changed to Name */}
                            <InputTextModal
                                required
                                label="Name"
                                name="description" // backend still reads description
                                onChange={handleChangeDescription}
                                value={newChecklistItem.description}
                            />

                            <InputTextModal
                                required={false}
                                type="url"
                                label="Link URL (optional)"
                                name="url"
                                onChange={handleChangeLink}
                                value={newLinkUrl}
                            />

                            {/* Buttons: Cancel then Save */}
                            <div className="flex space-x-4">
                                <ButtonModal
                                    name="Cancel"
                                    type="button"
                                    onClick={onHandleCancel}
                                    className="inline-flex justify-center flex-1 px-4 py-2 text-base font-medium text-gray-700 transition bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none"
                                />
                                <button
                                    type="submit"
                                    ref={completeButtonRef}
                                    disabled={isSaving}
                                    className={`flex-1 inline-flex justify-center items-center px-4 py-2 text-base font-medium text-white rounded-md shadow-sm focus:outline-none transition-colors ${
                                        isSaving
                                            ? "bg-teal-300 cursor-not-allowed"
                                            : "bg-teal-500 hover:bg-teal-600"
                                    }`}
                                >
                                    {isSaving && (
                                        <div className="w-4 h-4 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                                    )}
                                    {isSaving ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </form>
                    </Dialog.Panel>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default AddNewItemToJobModal;
